import { ChevronLeftIcon, ChevronRightIcon, InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  Flex,
  HStack,
  IconButton,
  Image,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  SimpleGrid,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Spinner,
  Text,
  Tooltip,
  UnorderedList,
  useBreakpointValue,
  useColorModeValue,
  VStack
} from '@chakra-ui/react';
import DriveModal from 'components/drive/DriveModal';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { GiWoodFrame } from 'react-icons/gi';
import { IoImagesOutline } from 'react-icons/io5';
import { useGetCatalogueProductListQuery, useGetProductDetailsQuery } from 'store/campaigns/adsApi';
import { styleForOverlayPosition } from 'utils/functions';

// constants
const pageSize = 6;
const gridPositions = [
  ['top_left', 'top', 'top_right'],
  ['left', 'center', 'right'],
  ['bottom_left', 'bottom', 'bottom_right'],
];
const uiBlendToSpec = (ui) => (ui === 'darken' ? 'multiply' : ui); // 'normal'|'lighten' unchanged
const specBlendToUi = (spec) => (spec === 'multiply' ? 'darken' : spec); // 'normal'|'lighten' unchanged

/* ---------------- reducer setup ---------------- */

const initialState = {
  // pagination / cursors
  after: '',
  before: '',
  pageIndex: 0,

  // drive modal
  isDriveModalOpen: false,

  // frame controls
  frameMode: 'None',      // 'None' | 'Custom'
  frameSrc: '',             // selected file object from DriveModal
  onlyShowWhenEngaging: true,
  blend: 'darken',          // css mix-blend-mode
  opacity: 100,             // 50..100
  size: 100,                // 10..100 (percentage of preview box)
  overlayPosition: 'center', // 'top','top_left','top_right','left','center','right','bottom','bottom_left','bottom_right'
};

function reducer(state, action) {
  switch (action.type) {
    case 'RESET_ON_OPEN':
      return { ...state, after: '', before: '', pageIndex: 0 };
    case 'SET_AFTER_CURSOR':
      return { ...state, after: action.payload, before: '' };
    case 'SET_BEFORE_CURSOR':
      return { ...state, before: action.payload, after: '' };
    case 'PAGE_NEXT':
      return { ...state, pageIndex: state.pageIndex + 1 };
    case 'PAGE_PREV':
      return { ...state, pageIndex: Math.max(0, state.pageIndex - 1) };

    case 'OPEN_DRIVE_MODAL':
      return { ...state, isDriveModalOpen: true };
    case 'CLOSE_DRIVE_MODAL':
      return { ...state, isDriveModalOpen: false };

    case 'SET_FRAME_MODE':
      return { ...state, frameMode: action.payload };
    case 'SET_FRAME_SRC':
      return { ...state, frameSrc: action.payload };
    case 'REMOVE_FRAME':
      return { ...state, frameSrc: '', frameMode: 'None' };

    case 'SET_ONLY_SHOW':
      return { ...state, onlyShowWhenEngaging: action.payload };
    case 'SET_BLEND':
      return { ...state, blend: action.payload };
    case 'SET_OPACITY': {
      const n = Number.isNaN(action.payload) ? 50 : Math.round(action.payload);
      return { ...state, opacity: Math.min(100, Math.max(50, n)) };
    }
    case 'SET_SIZE': {
      const n = Number.isNaN(action.payload) ? 10 : Math.round(action.payload);
      return { ...state, size: Math.min(100, Math.max(10, n)) };
    }
    case 'SET_OVERLAY_POSITION': {
      return { ...state, overlayPosition: action.payload };
    }
    default:
      return state;
  }
}

const PositionPicker = ({ value, onChange, lineColor = 'gray.200' }) => (
  <Flex direction="column">
    {gridPositions.map((row, rIdx) => (
      <Flex key={rIdx}>
        {row.map((pos, cIdx) => {
          const selected = value === pos;
          return (
            <Box
              as="button"
              key={pos}
              w="32px"
              h="32px"
              borderTop={rIdx === 0 ? '1px solid' : 'none'}
              borderLeft={cIdx === 0 ? '1px solid' : 'none'}
              borderRight="1px solid"
              borderBottom="1px solid"
              borderColor={lineColor}
              bg={selected ? '#8671cc' : 'white'}
              _dark={{ bg: selected ? '#8671cc' : 'gray.800' }}
              display="flex"
              alignItems="center"
              justifyContent="center"
              onClick={() => onChange(pos)}
              borderRadius={rIdx === 0 && cIdx === 0 ? 'sm' : 'none'}
            >
              {selected && <Box w="8px" h="8px" bg="white" borderRadius="full" />}
            </Box>
          );
        })}
      </Flex>
    ))}
  </Flex>
);


function CatalogueFrame({
  isOpen,
  onClose,
  productSetId,
  selectedAdAccounts,
  productSetName = 'NA',
  productSetStats = 'NA',
  setFieldValue,
  values,
  selectedAccountId
}) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    after,
    before,
    pageIndex,
    isDriveModalOpen,
    frameMode,
    frameSrc,
    onlyShowWhenEngaging,
    blend,
    opacity,
    size,
    overlayPosition,
  } = state;

  // responsive helpers
  const isMobile = useBreakpointValue({ base: true, lg: false });
  const modalSize = useBreakpointValue({ base: 'full', md: '5xl', lg: '6xl' });
  const activeBg = useColorModeValue('brand.50', '#1e1530');

  const [blink, setBlink] = useState(false);

  useEffect(() => {
    setBlink(true);
    const timeout = setTimeout(() => {
      setBlink(false);
    }, 150); // total blink duration
    return () => clearTimeout(timeout);
  }, [blend]); 

  // reset cursors/page when modal opens or set changes
  useEffect(() => {
    if (isOpen) dispatch({ type: 'RESET_ON_OPEN' });
  }, [isOpen, productSetId]);

  // query params (only 1 cursor at a time)
  const queryParams = useMemo(() => {
    const base = { pageSize: String(pageSize), productSetId };
    if (after) base.after = after;
    else if (before) base.before = before;
    return base;
  }, [after, before, productSetId]);

  const {
    data: productList,
    isFetching: isproductListFetching,
    isLoading: isproductListLoading,
    error: productListerror,
    refetch: refetchProductList,
  } = useGetCatalogueProductListQuery(queryParams, { skip: !productSetId || !isOpen });

  const {
    data: productDetails,
  } = useGetProductDetailsQuery({ productSetId }, { skip: !productSetId || !isOpen });

  const items = productList?.data ?? [];
  const meta = productList?.meta;

  const canGoPrev = !!meta?.hasPrevPage && !isproductListFetching;
  const canGoNext = !!meta?.hasNextPage && !isproductListFetching;


  useEffect(() => {
    if (!isOpen) return;
  
    const currentLayer = values?.image_layer_specs?.find(
      l => l.adsAccountId === selectedAccountId
    );
  
    const frameLayer = Object.values(currentLayer?.layers || {}).find(
      l => l.layer_type === 'frame_overlay'
    );
  
    if (!frameLayer) return;
  
    dispatch({ type: 'SET_FRAME_MODE', payload: 'Custom' });
    dispatch({ type: 'SET_BLEND', payload: specBlendToUi(frameLayer.blending_mode || 'normal') });
    dispatch({ type: 'SET_OPACITY', payload: frameLayer.opacity ?? 100 });
    dispatch({ type: 'SET_SIZE', payload: frameLayer.scale ?? 100 });
    dispatch({ type: 'SET_OVERLAY_POSITION', payload: frameLayer.overlay_position || 'center' });
    dispatch({ type: 'SET_ONLY_SHOW', payload: frameLayer.frame_auto_show_enroll_status === 'OPT_IN' });
  
    if (frameLayer.frame_image_hash && !state.frameSrc) {
      dispatch({
        type: 'SET_FRAME_SRC',
        payload: { hash: frameLayer.frame_image_hash, name: frameLayer?.frame_name, url: frameLayer?.frame_url  },
      });
    }
  }, [isOpen, selectedAccountId]);

  const handleSave = () => {
    if (frameMode !== 'Custom') {
      // Clear the image_layer_specs for the selectedAccountId
      const existingSpecs = values?.image_layer_specs || [];
      const updatedSpecs = existingSpecs.filter(item => item.adsAccountId !== selectedAccountId);
      
      setFieldValue('image_layer_specs', updatedSpecs);
      onClose();
      return;
    }
  
    const imageLayer = {
      layer_type: 'image',
      image_source: 'catalog',
    };
  
    const hash = frameSrc?.hash;  
  
    if (hash) {
      const frameOverlayLayer = {
        layer_type: 'frame_overlay',
        blending_mode: uiBlendToSpec(blend),
        frame_image_hash: hash,
        frame_url: frameSrc?.url ?? '',
        frame_name: frameSrc?.name ?? '',
        frame_source: 'custom',
        frame_auto_show_enroll_status: onlyShowWhenEngaging ? "OPT_IN" : "OPT_OUT",
        opacity,
        overlay_position: overlayPosition,
        scale: size,
      };
  
      const combinedLayers = {
        imageLayer,
        frameOverlayLayer,
      };
  
      const existingSpecs = values?.image_layer_specs || [];
  
      const updatedSpecs = existingSpecs.map(item =>
        item.adsAccountId === selectedAccountId
          ? { ...item, layers: combinedLayers }
          : item
      );
  
      const isNew = !existingSpecs.some(item => item.adsAccountId === selectedAccountId);
      if (isNew) {
        updatedSpecs.push({ adsAccountId: selectedAccountId, layers: combinedLayers });
      }
  
      setFieldValue('image_layer_specs', updatedSpecs);
    }
  
    onClose();
  };
  

  const handleNext = () => {
    if (!meta?.hasNextPage || !meta?.after) return;
    dispatch({ type: 'SET_AFTER_CURSOR', payload: meta.after });
    dispatch({ type: 'PAGE_NEXT' });
  };

  const handlePrev = () => {
    if (!meta?.hasPrevPage || !meta?.before) return;
    dispatch({ type: 'SET_BEFORE_CURSOR', payload: meta.before });
    dispatch({ type: 'PAGE_PREV' });
  };

  const handleFileSelect = (file) => {
    if (file && file.length > 0) {
      const selectedFile = file[0];
      dispatch({ type: 'SET_FRAME_SRC', payload: { hash: selectedFile.creativeId, name: selectedFile?.name, url: selectedFile?.url }});
      dispatch({ type: 'SET_FRAME_MODE', payload: 'Custom' });
    }
    dispatch({ type: 'CLOSE_DRIVE_MODAL' });
  };

  const removeFrame = () => dispatch({ type: 'REMOVE_FRAME' });

  // UI helpers
  const cardBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const line = useColorModeValue('gray.200', 'whiteAlpha.300');
  const textMute = useColorModeValue('gray.600', 'gray.400');
  const backgroundColor = useColorModeValue('gray.50', 'gray.800');

  const getImageSrc = (p) =>
    (p.image_cdn_urls || []).find((x) => x.key === 'requested')?.value || p.image_url;

  // header label 1–3, 4–6… using pageIndex
  const totalItems = productDetails?.product_count ?? undefined;
  const startIndex = items.length ? pageIndex * pageSize + 1 : 0;
  const endIndex = items.length
    ? Math.min(pageIndex * pageSize + items.length, totalItems ?? pageIndex * pageSize + items.length)
    : 0;

  const previewHeader =
    totalItems != null
      ? `Previewing ${startIndex}–${endIndex} of ${totalItems} items`
      : `Previewing ${startIndex}–${endIndex} items`;

  /* ----------- panels ----------- */

  const MediaPanel = (
    <>
      <Box p={4} borderBottom="1px solid" borderColor={line}>
        <Text fontWeight="semibold" mb={3}>Media</Text>
        <Flex align="center" gap={3} p={3} mb={6}>
          <Box w="40px" h="40px" rounded="md" bg={backgroundColor} display="flex" alignItems="center" justifyContent="center">
            <IoImagesOutline />
          </Box>
          <Box>
            <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
              {productDetails?.name || productSetName}
            </Text>
            <Text fontSize="xs" color={textMute}>
              {productDetails?.product_count ? `${productDetails.product_count} items` : productSetStats}
            </Text>
          </Box>
        </Flex>
      </Box>
      <Box p={4}>
        <Text fontWeight="semibold" mb={3}>Creative tools</Text>
        <VStack align="stretch" spacing={2}>
          <Button leftIcon={<GiWoodFrame />} justifyContent="flex-start" variant="ghost" size="sm" colorScheme="brand" bg={activeBg}>
            Frame
          </Button>
        </VStack>
      </Box>
    </>
  );

  const FramePanel = (
    <Box p={4}>
      <Text fontWeight="semibold" mb={1}>Frame</Text>
      <Text fontSize="14px" mb={3}>
        Use an engaging graphic, like a border or your logo, to frame catalog items. Frames work best when they have a transparent background.
      </Text>
      <Text fontSize="12px" color={textMute} mb={3}>Frame is only supported for ads on Facebook.</Text>

      <Flex align="center" mb={1}>
        <Text fontWeight="bold">Image</Text>
        <Box>
          <Tooltip
            borderRadius={5}
            label={
              <Box fontSize="sm" p="5px">
                <Text>Frame your catalog items with an image from your account or upload a new one. Frames work best when they have limited text and a transparent background.</Text>
              </Box>
            }
            fontSize="md"
          >
            <InfoOutlineIcon pointerEvents="all" ml={2} />
          </Tooltip>
        </Box>
      </Flex>

      <Flex gap={4}>
        <Select
          size="sm"
          w="140px"
          borderRadius="md"
          mb={3}
          value={frameMode}
          onChange={(e) => dispatch({ type: 'SET_FRAME_MODE', payload: e.target.value })}
        >
          <option value="None">None</option>
          <option value="Custom">Custom</option>
        </Select>

        {frameMode === "None" ? null :
          <>
            {!frameSrc && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => dispatch({ type: 'OPEN_DRIVE_MODAL' })}
                isDisabled={!selectedAdAccounts?.length > 0}
              >
                Upload Image
              </Button>
            )}
          </>
        }
      </Flex>

      {frameMode === "None" ? null :
          <>
            {frameSrc ? (
              <>
                <Box border="1px solid" borderColor={line} rounded="md" p={3} mb={4}>
                  <HStack align="center" spacing={3}>
                    <Box w="56px" h="56px" border="1px solid" borderColor={line} rounded="md" overflow="hidden" bg={cardBg}>
                      <Image src={frameSrc?.url} alt="Frame" w="100%" h="100%" objectFit="cover" />
                    </Box>
                    <VStack align="start" spacing={1} flex={1}>
                      <Text fontSize="12px">{frameSrc?.name}</Text>
                      <Button size="xs" variant="link" colorScheme="brand" onClick={removeFrame}>Remove</Button>
                    </VStack>
                  </HStack>
                </Box>

                <Checkbox
                  size="sm"
                  colorScheme="brand"
                  isChecked={onlyShowWhenEngaging}
                  alignItems="flex-start"
                  onChange={(e) => dispatch({ type: 'SET_ONLY_SHOW', payload: e.target.checked })}
                >
                  <Flex mt={-1}>
                    <Text fontSize="14px">Only show the image when it’s likely to increase engagement.</Text>
                  </Flex>
                </Checkbox>

                <Flex align="center" my={2}>
                  <Text fontWeight="bold">Blending</Text>
                  <Box>
                    <Tooltip
                      borderRadius={5}
                      label={
                        <Box fontSize="sm" p="5px">
                          <Text>Combine colors between your background and foreground to create effects or show images more clearly in your ads.</Text>
                        </Box>
                      }
                      fontSize="md"
                    >
                      <InfoOutlineIcon pointerEvents="all" ml={2} />
                    </Tooltip>
                  </Box>
                </Flex>

                <Select
                  size="sm"
                  w="140px"
                  borderRadius="md"
                  mb={4}
                  value={blend}
                  onChange={(e) => dispatch({ type: 'SET_BLEND', payload: e.target.value })}
                >
                  <option value="normal">None</option>
                  <option value="darken">Darken</option>
                  <option value="lighten">Lighten</option>
                </Select>

                {/* Opacity */}
                <Text fontWeight="semibold" mb={2}>Opacity</Text>
                <HStack spacing={4}>
                  <Box flex="1">
                    <Slider
                      aria-label="opacity"
                      colorScheme="brand"
                      value={opacity}
                      onChange={(v) => dispatch({ type: 'SET_OPACITY', payload: v })}
                      min={50}
                      max={100}
                      step={1}
                    >
                      <SliderTrack><SliderFilledTrack /></SliderTrack>
                      <Tooltip hasArrow placement="top" label={`${opacity}`} bg="gray.700" color="white" fontSize="xs">
                        <SliderThumb />
                      </Tooltip>
                    </Slider>
                    <Flex justify="space-between">
                      <Text fontSize="xs" color="gray.500">50</Text>
                      <Text fontSize="xs" color="gray.500">100</Text>
                    </Flex>
                  </Box>

                  <NumberInput
                    size="sm"
                    w="70px"
                    value={opacity}
                    min={50}
                    max={100}
                    step={1}
                    clampValueOnBlur
                    keepWithinRange
                    onChange={(_, v) => dispatch({ type: 'SET_OPACITY', payload: v })}
                  >
                    <NumberInputField borderRadius="md" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </HStack>

                {/* Size */}
                <Text fontWeight="semibold" mt={4} mb={2}>Size</Text>
                <HStack spacing={4}>
                  <Box flex="1">
                    <Slider
                      aria-label="size"
                      colorScheme="brand"
                      value={size}
                      onChange={(v) => dispatch({ type: 'SET_SIZE', payload: v })}
                      min={10}
                      max={100}
                      step={1}
                    >
                      <SliderTrack><SliderFilledTrack /></SliderTrack>
                      <Tooltip hasArrow placement="top" label={`${size}`} bg="gray.700" color="white" fontSize="xs">
                        <SliderThumb />
                      </Tooltip>
                    </Slider>
                    <Flex justify="space-between">
                      <Text fontSize="xs" color="gray.500">10</Text>
                      <Text fontSize="xs" color="gray.500">100</Text>
                    </Flex>
                  </Box>

                  <NumberInput
                    size="sm"
                    w="70px"
                    value={size}
                    min={10}
                    max={100}
                    step={1}
                    clampValueOnBlur
                    keepWithinRange
                    onChange={(_, v) => dispatch({ type: 'SET_SIZE', payload: v })}
                  >
                    <NumberInputField borderRadius="md" />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </HStack>

                <Text fontWeight="semibold" mt={4} mb={2}>
                  Position
                </Text>
                <PositionPicker
                  value={overlayPosition}
                  lineColor={line}
                  onChange={(pos) => dispatch({ type: 'SET_OVERLAY_POSITION', payload: pos })}
                />

              </>
            ) : (
              <Flex
                direction="column"
                align="flex-start"
                p={4}
                mt={2}
                rounded="md"
                border="1px solid"
                borderColor={line}
                bg={cardBg}
                w="100%"
              >
                <HStack spacing={2} mb={2}>
                  <Text fontWeight="semibold" color="gray.500">IMAGE SPECIFICATIONS</Text>
                </HStack>

                <UnorderedList spacing={2} stylePosition="outside" ml={5} fontSize="sm">
                  <ListItem><Text>Recommended image size: <Box as="span" fontWeight="bold">1080 × 1080 pixels</Box></Text></ListItem>
                  <ListItem><Text>Required file size: <Box as="span" fontWeight="bold">Under 1MB</Box></Text></ListItem>
                  <ListItem><Text>Recommended aspect ratio: <Box as="span" fontWeight="bold">1:1</Box></Text></ListItem>
                  <ListItem><Text>Recommended background color: <Box as="span" fontWeight="bold">Transparent</Box></Text></ListItem>
                </UnorderedList>
              </Flex>
            )}
          </>
      }
    </Box>
  );

  /* ---------- preview grid ---------- */

  const PreviewGrid = () => {
    if (isproductListLoading || isproductListFetching) return <Spinner />;
    if (productListerror) {
      return (
        <VStack>
          <Text color="red.400" fontSize="sm">Failed to load products.</Text>
          <Button onClick={() => refetchProductList()} size="sm" variant="outline">Retry</Button>
        </VStack>
      );
    }
    if (!items.length) return <Text color={textMute}>No items found.</Text>;

    return (
      <SimpleGrid columns={{ base: 1, sm: 2, md: 2 }} spacing={4} w="100%">
        {items.map((it) => (
          <Box key={it.id} position="relative" w="100%" style={{ aspectRatio: '1 / 1' }} overflow="hidden" rounded="md">
            <Image src={getImageSrc(it)} alt={it.name} w="100%" h="100%" objectFit="cover" />
            {frameMode === 'Custom' && frameSrc && (
              <Image
                key={blend} 
                src={frameSrc?.url}
                alt="Frame overlay"
                position="absolute"
                w={`${size}%`}
                h={`${size}%`}
                objectFit="cover"
                pointerEvents="none"
                style={{
                  ...styleForOverlayPosition(overlayPosition),
                  mixBlendMode: 'darken', 
                  opacity: blink ? 0 : opacity / 100,
                  transition: 'opacity 0.15s ease-in-out',
                }}
              />
            )}
          </Box>
        ))}
      </SimpleGrid>
    );
  };

  return (
    <Box>
      <DriveModal
        isDriveModalOpen={isDriveModalOpen}
        selectedAdAccounts={selectedAdAccounts}
        onClose={() => dispatch({ type: 'CLOSE_DRIVE_MODAL' })}
        onSubmit={handleFileSelect}
        uploadPurpose="update"
        mediaType="image"
      />

      <Modal isOpen={isOpen} onClose={onClose} size={modalSize} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Edit creative</ModalHeader>
          <ModalCloseButton />

          <ModalBody p={0}>
            {isMobile ? (
              <Box>
                <Accordion allowToggle defaultIndex={[0]}>
                  <AccordionItem borderTop="none">
                    <AccordionButton px={4} py={3} borderTop="1px solid" borderColor={line}>
                      <Text flex="1" textAlign="left" fontWeight="semibold">Media</Text>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel px={0} pb={4}>{MediaPanel}</AccordionPanel>
                  </AccordionItem>

                  <AccordionItem>
                    <AccordionButton px={4} py={3} borderTop="1px solid" borderColor={line}>
                      <Text flex="1" textAlign="left" fontWeight="semibold">Frame</Text>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel px={0} pb={4}>{FramePanel}</AccordionPanel>
                  </AccordionItem>
                </Accordion>

                <Box borderTop="1px solid" borderColor={line}>
                  <Flex align="center" justify="space-between" px={4} py={3}>
                    <Text fontWeight="semibold" fontSize="sm" noOfLines={1}>{previewHeader}</Text>
                    <HStack>
                      <IconButton aria-label="Prev" icon={<ChevronLeftIcon />} size="sm" variant="outline" onClick={handlePrev} isDisabled={!canGoPrev} />
                      <IconButton aria-label="Next" icon={<ChevronRightIcon />} size="sm" variant="outline" onClick={handleNext} isDisabled={!canGoNext} />
                    </HStack>
                  </Flex>
                  <Flex align="center" justify="center" p={4}><PreviewGrid /></Flex>
                </Box>
              </Box>
            ) : (
              <Flex gap={0} minH="70vh">
                <Box w="300px" borderColor={line}>{MediaPanel}</Box>
                <Box w="360px" borderColor={line}>{FramePanel}</Box>

                <Box flex="1" minW={0} p={0} bg={backgroundColor} borderRadius="md">
                  <Flex align="center" justify="space-between" px={4} py={3} borderBottom="1px solid" borderColor={line}>
                    <Text fontWeight="semibold" fontSize="sm">{previewHeader}</Text>
                    <HStack>
                      <IconButton aria-label="Prev" icon={<ChevronLeftIcon boxSize={5} />} size="sm" variant="outline" onClick={handlePrev} isDisabled={!canGoPrev} />
                      <IconButton aria-label="Next" icon={<ChevronRightIcon boxSize={5} />} size="sm" variant="outline" onClick={handleNext} isDisabled={!canGoNext} />
                    </HStack>
                  </Flex>
                  <Flex align="center" justify="center" p={4}><PreviewGrid /></Flex>
                </Box>
              </Flex>
            )}
          </ModalBody>

          <ModalFooter>
            <Button onClick={onClose} mr={3} variant="ghost" size="sm">Cancel</Button>
            <Button colorScheme="brand" onClick={handleSave} size="sm">Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default CatalogueFrame;
